/**
 * VideoUploadProgressOverlay
 *
 * Fixed-position progress bar anchored at the bottom of the screen while a
 * user video is uploading. Mirrors Flutter's `AppOverlay.videoUploadingOverlay`
 * + `VideoUploadProgressNotifier` (app_overlay.dart / video_upload_progress_notifier.dart),
 * but rendered at the bottom per task requirement #4.
 *
 * Reads state from `uploadProgressSlice` — hidden when `isUploading` is false.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { selectUploadProgress } from '@/presentation/store/slices/uploadGlobalOverlayProgress/uploadGlobalOverlayProgressSlice';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const VideoUploadProgressOverlay: React.FC = () => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const { isUploading, progress, message } = useSelector(selectUploadProgress);

  if (!isUploading) return null;

  const label = message ?? l10n.publish.uploadingVideo;
  const trackHeight = responsive.spacing['2xl'];

  // Lifecycle status derived purely from existing slice state: while bytes are
  // still in flight (`progress < 100`) the overlay is `uploading`; once they're
  // fully transferred (`progress >= 100`) it's `processing` (server-side
  // finalization). The overlay unmounts on `endUpload`, and the slice tracks no
  // error flag, so `done`/`error` are not representable here (see task note).
  const uploadStatus = progress >= 100 ? 'processing' : 'uploading';

  return (
    <div
      {...qaAttr({ id: 'upload-progress', value: progress, status: uploadStatus })}
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: trackHeight,
          backgroundColor: theme.modalOverlay,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: theme.exclusive + '90',
            transition: 'width 120ms linear',
          }}
        />
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: responsive.spacing.base,
            transform: 'translateY(-50%)',
            color: theme.text.primary,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.medium,
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );
};
